import { BookOpen, UserPlus, Server, Boxes, UserX, KeyRound, LifeBuoy, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND_NAME } from "@/lib/brand";

// Inline **bold** renderer for step text.
const fmt = (text) => String(text).split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
  p.startsWith("**") && p.endsWith("**")
    ? <strong key={i} className="text-zinc-100 font-semibold">{p.slice(2, -2)}</strong>
    : <span key={i}>{p}</span>);

const SECTIONS = [
  {
    id: "signin", title: "Signing in", icon: ShieldCheck,
    intro: "Administrators and staff sign in with the email and password issued to them.",
    steps: [
      "Go to the portal URL and enter your **email** and **password**, then click **Sign in**.",
      "Operators (owners, managers, technicians) land on the **Command Center**. End users land on their **Support** portal.",
      "Change your password after first sign-in if you were given a temporary one.",
    ],
  },
  {
    id: "add-user", title: "Add a user", icon: UserPlus,
    intro: "Create accounts for your own staff (operators) or for the people you support (end users). Only owners/managers can add users.",
    steps: [
      "Open **Users & Access** in the sidebar (Administration).",
      "On the **Create user** tab, enter the person's **name** and **email**.",
      "Pick a **role**:",
      "   • **Operators** — MSP Owner, Service Desk Manager, L1/L2/L3 Technician, Security Analyst, Compliance Officer.",
      "   • **End users** — Client Read-Only (typical), Client Manager, or Client Administrator.",
      "If you pick an **end-user (Client) role**, a **Company** selector appears — choose the company that person belongs to. This scopes them to that company.",
      "Leave the **password** blank to auto-generate a one-time temp password (shown **once** — copy it and hand it over), or type one yourself.",
      "Click **Create user**.",
    ],
    notes: [
      "End users only ever see their **own** requests and can't reach any operator screens.",
      "Owner/Admin roles can only be assigned by an owner.",
    ],
  },
  {
    id: "manage-users", title: "Manage users", icon: KeyRound,
    intro: "Change roles, disable access, reset passwords, or remove accounts from the Users & Access table.",
    steps: [
      "In **Users & Access**, find the person (use the search box).",
      "**Change role** — pick a new role from the dropdown on their row.",
      "**Deactivate / Activate** — click the status pill to toggle access. A deactivated user is signed out and blocked from logging in.",
      "**Reset password** (key icon) — generates a new one-time temp password, shown once.",
      "**Delete** (trash icon) — permanently removes the account.",
    ],
    notes: [
      "You can't deactivate or delete your own account, or remove the last owner.",
    ],
  },
  {
    id: "assign-agent", title: "Assign an endpoint to a company", icon: Server,
    intro: "Laptops are pre-imaged with the agent, then assigned to a company when they ship. This maps the device to the right client.",
    steps: [
      "Open **Agent Deployment** (or **Asset Lifecycle → the endpoint**) and open the endpoint.",
      "Go to the **Onboarding** tab.",
      "Under **Assign endpoint to a company**, choose the **Company** and, optionally, the **Branch / Location**.",
      "Click **Assign endpoint**. The device (and its asset record) is now scoped to that company.",
    ],
    notes: [
      "This doesn't change anything on the machine — it only maps it to the company in the console.",
    ],
  },
  {
    id: "deploy-agent", title: "Deploy the agent to a new machine", icon: Server,
    intro: "Generate an installer to enroll a new endpoint.",
    steps: [
      "Open **Agent Deployment**.",
      "Under **Build Installer**, pick the **Company** (and Branch), the **platform** (Windows / macOS / Linux), and whether it's a **reusable** installer (one installer for many machines) or single-use.",
      "Click **Generate Installer**.",
      "**Download** the agent for that platform, or copy the **one-line install** command and run it on the endpoint (elevated PowerShell on Windows, sudo shell on macOS/Linux).",
      "The endpoint appears under **Enrolled Agents** once it checks in.",
    ],
  },
  {
    id: "onboard-software", title: "Install a company's software set", icon: Boxes,
    intro: "Define standard software once, then install it on any endpoint for that company.",
    steps: [
      "Create the list under **Administration → Install Templates** (name it, add apps with a method: winget/choco/brew/download-URL/command).",
      "On the **company page** (Clients → the company → **Onboarding Software**), tick the template(s) that make up its standard build and **Save**.",
      "On the endpoint's **Onboarding** tab, click **Run onboarding** to install the company's set. Progress shows per-app status.",
    ],
  },
  {
    id: "offboard", title: "Offboard a departing user", icon: UserX,
    intro: "Lock the account, extract their data, and wipe their profile on a managed endpoint. Restricted to security & service-desk managers; every action is logged.",
    steps: [
      "Open the endpoint → **Offboarding** tab.",
      "Enter the **target account username** on that machine.",
      "Choose an action: **Lock account**, **Reset password**, **Force logoff**, **Extract user data** (zips their profile and uploads it here to download), or **Wipe user data** (removes their profile + disables the account — confirmation required).",
      "Download any extracted archive from the **Collected data** list.",
    ],
    notes: [
      "Wipe affects the **departing user's data only** — never the whole device.",
    ],
  },
  {
    id: "enduser", title: "What end users see (Support portal)", icon: LifeBuoy,
    intro: "People you support get a simple self-service portal — not the operator console.",
    steps: [
      "They sign in and land on **My Requests**.",
      "**Submit a request** — subject, category, priority, details.",
      "They can open any of their requests, read replies, and **reply** back. Replying to a resolved request reopens it.",
      "They only ever see their **own** requests.",
    ],
  },
];

export default function UserManual() {
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Administration</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1 flex items-center gap-2"><BookOpen size={24} className="text-blue-400" /> User Manual</h1>
        <p className="text-sm text-zinc-500 mt-1">How to run {BRAND_NAME} day to day — adding users, assigning endpoints, and the common admin tasks.</p>
      </div>

      {/* Contents */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Contents</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {SECTIONS.map(s => (
            <a key={s.id} href={`#${s.id}`} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-blue-400">
              <s.icon size={14} className="text-zinc-500" /> {s.title}
            </a>
          ))}
        </div>
      </div>

      {SECTIONS.map(s => (
        <section key={s.id} id={s.id} className="bg-zinc-950 border border-zinc-900 rounded-md p-6 scroll-mt-20">
          <div className="flex items-center gap-2">
            <s.icon size={18} className="text-blue-400" />
            <h2 className="font-display text-xl font-semibold tracking-tight">{s.title}</h2>
          </div>
          {s.intro && <p className="text-sm text-zinc-400 mt-2">{s.intro}</p>}
          <ol className="mt-4 space-y-1.5">
            {s.steps.map((step, i) => {
              const sub = step.startsWith("   ");
              return (
                <li key={i} className={"text-sm text-zinc-300 " + (sub ? "pl-10" : "flex gap-3")}>
                  {!sub && <span className="text-zinc-600 font-mono shrink-0 w-5 text-right">{s.steps.slice(0, i + 1).filter(x => !x.startsWith("   ")).length}.</span>}
                  <span>{fmt(sub ? step.trim() : step)}</span>
                </li>
              );
            })}
          </ol>
          {s.notes && s.notes.length > 0 && (
            <div className="mt-4 border-t border-zinc-900 pt-3 space-y-1">
              {s.notes.map((n, i) => <div key={i} className="text-[13px] text-zinc-500 flex gap-2"><span className="text-blue-400">›</span><span>{fmt(n)}</span></div>)}
            </div>
          )}
        </section>
      ))}

      <div className="text-center text-xs text-zinc-600 pb-6">
        Need something not covered here? Open <Link to="/docs" className="text-blue-400 hover:underline">Knowledge Base</Link> or contact your administrator.
      </div>
    </div>
  );
}
